import { useEffect, useRef, useState } from 'react'

import { useCopyToClipboard } from '@uidotdev/usehooks'
import { cva } from 'class-variance-authority'
import { Check, Copy } from 'lucide-react'

import { cn } from '@/utils/ui/styles'

import { time } from '@/shared/datetime/utils/time'

import { Button } from '../ui/button'

import type { ButtonProps } from '../ui/button'
import type { VariantProps } from 'class-variance-authority'

const copyButtonVariants = cva('aspect-square', {
	variants: {
		variant: {
			outline:
				'bg-background dark:bg-background hover:bg-background/50 dark:hover:bg-primary',
			ghost: '',
			default: '',
			secondary: '',
			destructive: '',
			link: '',
		},
	},
	defaultVariants: {
		variant: 'outline',
	},
})

type CopyButtonProps = Omit<ButtonProps, 'type'> &
	VariantProps<typeof copyButtonVariants> & {
		text: string
		iconClassName?: string
	}

/**
 * A button that copies `text` to the clipboard, showing a transient check icon.
 * @param text - The text to copy
 *
 * Note: any `onClick` passed by the caller is intentionally dropped (`onClick: _`)
 * — the button owns its click handler. Pass copy-side-effects via `text` only.
 */
export const CopyButton = ({
	text,
	className,
	iconClassName,
	size = 'icon',
	variant = 'outline',
	onClick: _,
	...props
}: CopyButtonProps) => {
	const [__, copyToClipboard] = useCopyToClipboard()
	const [canCopy, setCanCopy] = useState<boolean>(true)
	const timeoutRef = useRef<NodeJS.Timeout | null>(null)

	useEffect(() => {
		return () => {
			if (timeoutRef.current) clearTimeout(timeoutRef.current)
		}
	}, [])

	const handleCopy = () => {
		setCanCopy(false)
		copyToClipboard(text)

		timeoutRef.current = setTimeout(
			() => {
				setCanCopy(true)
			},
			time(2, 's'),
		)
	}

	return (
		<Button
			type="button"
			className={cn(copyButtonVariants({ variant, className }))}
			title="Copy"
			name="copy"
			nativeButton
			size={size}
			variant={variant}
			{...props}
			onClick={handleCopy}
		>
			{canCopy ? (
				<Copy
					data-testid="copy-icon"
					className={cn('animate-in h-4 w-4 duration-200', iconClassName)}
				/>
			) : (
				<Check
					data-testid="check-icon"
					className={cn(
						'direction-normal repeat-1 h-4 w-4 animate-pulse text-green-500 duration-200',
						iconClassName,
					)}
				/>
			)}
		</Button>
	)
}
